/**
 * Settings → Connectors.
 *
 * One home for every outbound integration. The left rail lists the registered
 * drivers with live health; the right pane renders the shared ConnectorConsole,
 * so a connector added on the backend appears here with no UI work.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import api from '@/lib/api';
import { toast } from 'sonner';
import {
  Plug, Loader2, RefreshCw, AlertTriangle, CheckCircle2, CircleDashed, Download,
} from 'lucide-react';
import ConnectorConsole, { maskVendor } from '@/components/connectors/ConnectorConsole';
import { ScheduleImportPanel } from '@/components/connectors/ScheduleImportPanel';
import { ContractErpSyncPanel } from '@/components/connectors/ContractErpSyncPanel';

const GROUPS = [
  { id: 'project_controls', title: 'Schedule' },
  { id: 'contracts', title: 'Contracts & claims' },
  { id: 'erp', title: 'ERP & finance' },
  { id: 'ipt', title: 'Invoices' },
  { id: 'nfa', title: 'Approvals' },
  { id: 'other', title: 'Models & documents' },
];

const groupOf = (c) => (GROUPS.some(g => g.id === c.module) ? c.module : 'other');

export default function SettingsConnectors() {
  const [params, setParams] = useSearchParams();
  const [connectors, setConnectors] = useState([]);
  const [loading, setLoading] = useState(true);
  const active = params.get('c');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/connectors');
      setConnectors(data.connectors || []);
    } catch (e) {
      toast.error(e?.response?.status === 403
        ? 'The connector console is administrative — your role cannot open it.'
        : 'Could not load the connectors');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (active || !connectors.length) return;
    // Pick the first connector as the rail renders it, not as the API returns it.
    const order = GROUPS.map(g => g.id);
    const first = [...connectors].sort(
      (a, b) => order.indexOf(groupOf(a)) - order.indexOf(groupOf(b)))[0];
    setParams({ c: first.name }, { replace: true });
  }, [active, connectors, setParams]);

  const current = useMemo(
    () => connectors.find(c => c.name === active) || null, [connectors, active]);

  const grouped = useMemo(() => {
    const out = {};
    for (const c of connectors) (out[groupOf(c)] ||= []).push(c);
    return out;
  }, [connectors]);

  const canImport = current?.capabilities?.includes('schedule_import');

  return (
    <DashboardLayout>
      <div className="mb-5 flex items-start gap-3">
        <Plug className="w-5 h-5 text-sky-500 mt-1" />
        <div>
          <h2 className="font-heading text-2xl font-bold" data-testid="connectors-title">
            Connectors
          </h2>
          <p className="text-slate-500 text-sm">
            Every outbound integration — schedule, ERP, invoices, approvals and the
            model service — configured, tested and monitored in one place
          </p>
        </div>
        <button onClick={load}
                className="ml-auto mt-1 text-slate-400 hover:text-slate-100 transition"
                data-testid="connectors-refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {loading ? (
        <div className="h-96 loading-shimmer rounded-xl" />
      ) : !connectors.length ? (
        <div className="glass-card p-8 text-center text-slate-400" data-testid="connectors-empty">
          No connectors are registered on this deployment.
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
          <nav className="lg:col-span-3 space-y-4" data-testid="connectors-rail">
            {GROUPS.filter(g => grouped[g.id]?.length).map(g => (
              <div key={g.id}>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5 px-1">
                  {g.title}
                </p>
                <div className="space-y-1">
                  {grouped[g.id].map(c => {
                    const ok = c.health?.ok;
                    const mode = String(c.health?.mode || '').toUpperCase();
                    // Only a LIVE connector missing fields is "not configured" —
                    // a simulated one has its own story and must tell it here,
                    // or the rail and the console contradict each other.
                    const unconfigured = mode === 'LIVE'
                      && !!(c.health?.missingForLive?.length);
                    const subtitle = unconfigured
                      ? 'Not configured yet'
                      : maskVendor(String(c.health?.labelled || mode || ''));
                    return (
                      <button key={c.name}
                              onClick={() => setParams({ c: c.name })}
                              className={`w-full text-left px-3 py-2 rounded-lg border transition-colors ${
                                active === c.name
                                  ? 'border-sky-500/60 bg-sky-500/10'
                                  : 'border-white/[0.06] hover:border-white/20'}`}
                              data-testid={`connector-tab-${c.name}`}>
                        <div className="flex items-center gap-2">
                          {unconfigured
                            ? <CircleDashed className="w-3.5 h-3.5 text-slate-500 shrink-0" />
                            : ok
                              ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
                              : <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0" />}
                          <span className="text-xs font-medium truncate">
                            {maskVendor(c.label)}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-500 mt-0.5 truncate">{subtitle}</p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </nav>

          <div className="lg:col-span-9 space-y-5">
            {canImport && (
              <ScheduleImportPanel connector={current.name} label={current.label} />
            )}
            {current?.capabilities?.includes('contract_sync') && (
              <ContractErpSyncPanel connector={current.name} label={current.label} />
            )}
            {current && (
              <ConnectorConsole key={current.name} name={current.name}
                                title={maskVendor(current.label)} />
            )}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
